import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { formatCents } from '@pirouette/shared';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { useShows, useDances } from '@/data/productions';
import {
  useRecitalDashboard,
  useQuickChangeConflicts,
  type RecitalCostumeReadiness,
  type RecitalDashboard,
  type QuickChangeConflictRow,
} from '@/data/recital';

// Recital Operations dashboard. A child of the production detail route (like
// conflicts / by-dancer / by-family), so it inherits the /admin `admin`-role
// guard. Two surfaces:
//   1. Readiness header — counts (routines / dancers / shows / quick-change
//      conflicts) plus the costume readiness percentages, reusing the same
//      StatCards as the Costume Command Center so it reads as one product.
//   2. Quick-Change panel — pick a show, list its quick_change_conflicts with
//      severity tone badges, resolving dance names client-side via useDances.
export const Route = createFileRoute('/admin/productions/$id/recital')({
  component: RecitalOps,
});

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ---------------------------------------------------------------------------
// Layout primitives (mirror admin.bi.index.tsx / admin.costumes.index.tsx)
// ---------------------------------------------------------------------------

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <article className="surface-elevated p-5">
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{label}</p>
      <p className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{hint}</p>}
    </article>
  );
}

function Section({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// `total` may be 0 for a fresh production; guard the percentage math (matches
// the Costume Command Center's `percent` helper).
function percent(part: number, total: number): string {
  if (total <= 0) return '—';
  return `${Math.round((part / total) * 100)}%`;
}

// ---------------------------------------------------------------------------
// Readiness header
// ---------------------------------------------------------------------------

function CountGrid({ dashboard }: { dashboard: RecitalDashboard }) {
  const quickTone: BadgeTone = dashboard.quick_change_conflicts > 0 ? 'warn' : 'success';
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard label="Routines" value={dashboard.routines} hint="Dances in this production" />
      <StatCard label="Dancers" value={dashboard.dancers} hint="Cast across all routines" />
      <StatCard label="Shows" value={dashboard.shows} hint="Performances scheduled" />
      <StatCard
        label="Quick-change conflicts"
        value={<Badge tone={quickTone}>{dashboard.quick_change_conflicts}</Badge>}
        hint="Tight back-to-back appearances"
      />
    </div>
  );
}

// Reuses the exact StatCard percentages the Costume Command Center renders, so
// the two surfaces stay visually consistent.
function CostumeReadiness({ costume }: { costume: RecitalCostumeReadiness }) {
  const total = costume.total;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <StatCard
        label="Measured"
        value={percent(costume.measured, total)}
        hint={`${costume.measured} of ${total} dancers`}
      />
      <StatCard
        label="Sized"
        value={percent(costume.sized, total)}
        hint={`${costume.sized} of ${total} sized`}
      />
      <StatCard
        label="Ordered"
        value={percent(costume.ordered, total)}
        hint={`${costume.ordered} of ${total} ordered`}
      />
      <StatCard
        label="Received"
        value={percent(costume.received, total)}
        hint={`${costume.received} of ${total} received`}
      />
      <StatCard
        label="Distributed"
        value={percent(costume.distributed, total)}
        hint={`${costume.distributed} of ${total} handed out`}
      />
      <StatCard
        label="Costume $ collected"
        value={formatCents(costume.paid_cents)}
        hint={`of ${formatCents(costume.cost_cents)} total cost`}
      />
      <StatCard
        label="Exceptions"
        value={costume.exceptions}
        hint="Missing / damaged / replacement"
      />
      <StatCard label="Total costumes" value={total} hint="Assignments in production" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick-Change panel
// ---------------------------------------------------------------------------

// RPC grades each conflict; map to the standard tone scale. `severity` is typed
// `string` in the generated contract, so fall back defensively.
function severityTone(severity: string): BadgeTone {
  if (severity === 'critical') return 'danger';
  if (severity === 'warning') return 'warn';
  return 'neutral';
}

function conflictDancerName(row: QuickChangeConflictRow): string {
  return `${row.dancer_first} ${row.dancer_last}`.trim() || 'Dancer';
}

function QuickChangeList({
  rows,
  danceName,
}: {
  rows: QuickChangeConflictRow[];
  danceName: (danceId: string, seq: number) => string;
}) {
  return (
    <ul className="grid gap-2">
      {rows.map((row) => (
        <li
          key={`${row.dancer_id}-${row.from_dance_id}-${row.to_dance_id}`}
          className="surface flex flex-wrap items-center justify-between gap-3 p-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={severityTone(row.severity)}>{row.severity}</Badge>
            <span>
              <span className="font-medium">{conflictDancerName(row)}</span>
              <span className="ml-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                {danceName(row.from_dance_id, row.from_seq)} →{' '}
                {danceName(row.to_dance_id, row.to_seq)}
              </span>
            </span>
          </div>
          <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)] tabular-nums">
            {row.slots_between} {row.slots_between === 1 ? 'slot' : 'slots'} between
          </span>
        </li>
      ))}
    </ul>
  );
}

function QuickChangePanel({ productionId }: { productionId: string }) {
  const shows = useShows(productionId);
  const dances = useDances(productionId);
  const [showId, setShowId] = useState('');
  const conflicts = useQuickChangeConflicts(showId || undefined);

  // Resolve dance names client-side by joining the production's dances. Fall
  // back to the sequence number when a dance can't be matched (e.g. removed
  // since the lineup was built) so the row still reads.
  const danceNameById = useMemo(
    () => new Map((dances.data ?? []).map((d) => [d.id, d.name])),
    [dances.data],
  );
  const danceName = (danceId: string, seq: number): string =>
    danceNameById.get(danceId) ?? `#${seq}`;

  const showOptions = shows.data ?? [];
  const conflictRows = conflicts.data ?? [];
  const hasShow = Boolean(showId);

  return (
    <Section
      id="quick-change"
      title="Quick changes"
      description="Per show, dancers who reappear within a couple of slots — too little time for a costume change. Critical conflicts are flagged in red, tighter-than-ideal ones in amber."
    >
      <label className="grid max-w-xl gap-2">
        <span className="font-medium text-[var(--text-sm)]">Show</span>
        <select
          className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
          value={showId}
          onChange={(event) => setShowId(event.target.value)}
        >
          <option value="">Choose a show</option>
          {showOptions.map((show) => (
            <option key={show.id} value={show.id}>
              {show.name} · {new Date(show.performance_at).toLocaleString()}
            </option>
          ))}
        </select>
      </label>

      {!hasShow ? (
        <Empty
          title="Select a show"
          description="Pick a show above to list its quick-change conflicts."
        />
      ) : conflicts.isLoading ? (
        <p aria-live="polite">Loading…</p>
      ) : conflicts.error ? (
        <Empty
          title="Quick changes unavailable"
          description="The quick-change report could not be loaded for this show."
        />
      ) : conflictRows.length === 0 ? (
        <Empty
          title="No quick-change conflicts"
          description="No dancer reappears too soon in this show. Conflicts will surface here as the running order tightens."
        />
      ) : (
        <QuickChangeList rows={conflictRows} danceName={danceName} />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Recital Ops
// ---------------------------------------------------------------------------

function RecitalOps() {
  const { id: productionId } = Route.useParams();
  const dashboard = useRecitalDashboard(productionId);

  const dashboardData = dashboard.data;

  return (
    <div className="space-y-10">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Production
        </Link>
        <p className="mt-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Recital operations
        </p>
        <h1 className="mt-1">Recital Ops</h1>
      </header>

      {/* Readiness counts ------------------------------------------------- */}
      <Section
        id="readiness"
        title="Readiness"
        description="Routines, dancers, and shows in this production, the count of open quick-change conflicts, and costume fulfillment progress."
      >
        {dashboard.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : dashboard.error || !dashboardData ? (
          <Empty
            title="Readiness unavailable"
            description="The recital dashboard could not be loaded for this production."
          />
        ) : (
          <div className="space-y-4">
            <CountGrid dashboard={dashboardData} />
            <CostumeReadiness costume={dashboardData.costume} />
          </div>
        )}
      </Section>

      {/* Quick changes ---------------------------------------------------- */}
      <QuickChangePanel productionId={productionId} />
    </div>
  );
}
